import {observable, action, observe} from "mobx";
import {autoSave, loadStore} from "../_core/mobxLocalStorage";
import meService from "../services/meService";
import userService from "../services/userService";
import baseRequest from "../_core/baseRequest";
import {b64EncodeUnicode} from "../utils/helpers";
import appStore from "./appStore";
import i18n from "../i18n";
import {cities} from "../constants/constants";
import {isPathGranted} from "../constants/pagePermissions";
import {
    TICKET_BACKOFFICE_ROLES,
    TICKET_ADMIN_ROLES,
    TICKET_PERMISSION_KEYS
} from "../constants/ticket";
import {
    APPLICATION_ROLES,
    APPLICATION_COMPANY_EDIT_ROLES,
    APPLICATION_ZONE_MANAGEMENT_FILTER_ROLES,
    APPLICATION_PERMISSION_KEYS
} from "../constants/application";
import {message} from "antd";
import optionStore from "./optionStore";
import {isDedicatedIntegrationCustomer} from "../utils/customerHelpers";

class meStore {
    constructor() {
        loadStore(this, "meStore");
        autoSave(this, "meStore");

        // Initialize courierDailyOperations with default values if not loaded from localStorage
        if (!this.courierDailyOperations) {
            this.courierDailyOperations = {
                paymentTypes: {
                    paid: true,
                    creditCard: true,
                    cash: true,
                    ticketRestaurant: true,
                    sodexo: true,
                    multinet: true,
                    setCard: true,
                    yemekmatik: true,
                    metropol: true,
                    cioCard: true,
                    paye: true,
                    winWin: true,
                    tokenFlex: true,
                    unspecified: true
                }
            };
        }

        observe(this, "permissionHash", async change => {
            if (!!change.newValue) {
                await this.getPermissionPolicies();
            }
        });
    }

    @observable map = {
        filters: {
            city: "istanbul",
            courierType: [],
            courierStatus: [],
            company: "",
            subCompany: "",
            courier: "",
            disabled: "all",
            workType: "dedicated"
        },
        settings: {
            center: {lat: 41.11, lng: 29.11},
            zoom: 11
        },
        options: {
            showSubCompanies: false,
            showDummyCouriers: false,
            showRegions: false
        }
    };

    @observable liveOperation = {
        filters: {
            city: "istanbul",
            company: "",
            subCompany: [],
            pool: [],
            zoneManagementId: undefined,
            zoneId: undefined
        },
        options: {
            showSubCompanies: false,
            showOrders: false,
            showCouriers: true
        }
    };

    @observable searchTextFilter = "";
    @observable orderPage = {
        filters: {
            orderType: "current",
            deliveryStatusOverrides: [],
            companies: [],
            selectedSubCompanies: [],
            dateRange: []
        }
    };

    @observable courierDailyOperations = undefined;

    @observable isLoggedIn = false;
    @observable fullname = "";
    @observable username = "";
    @observable email = "";
    @observable company = undefined;
    @observable companies = [];
    @observable subcompanies = [];
    @observable sessionToken = undefined;
    @observable userId = undefined;
    @observable role = "";
    // Alt kullanıcı (Acente Temsilcisi): backend rolünü "agency" olarak döner, böylece tüm rol
    // kontrolleri acente ile aynı çalışır; sayfa/aksiyon kısıtı bu bayrakla uygulanır.
    @observable isSubAgency = false;
    @observable auth = undefined;
    @observable permissionHash = undefined;
    @observable permissionPolicies = [];
    @observable isPoolCustomer = false;
    @observable isUatCustomer = false;
    @observable hasCourierDailyOperations = false;
    @observable selectedSubCompanyId = null;
    @observable canCallCouriers = false; // Flag to control courier calling ability in panel
    @observable alotech = null; // AloTech configuration for the user
    @observable operationPrivileges = []; // AppConfig'den e-posta bazlı verilen ek operationId yetkileri
    //cod payment selection kontrolü yapıldı mı?
    @observable hasCodPaymentMethodSelectionChecked = false;
    @observable isDedicatedCustomer = false;

    /**
     * @typedef {object} canOptions Options for the can func.
     * @property {("none"|"message"|"modal"|"redirection")}  responseType Error response type
     * @property {string}  errorContent Error response message content
     * @property {string}  redirectTo Redirection path
     * @param {string} permissionKey Permission key that will be checked.
     * @param {canOptions} options
     */
    @action can = (
        permissionKey,
        {responseType = "none", errorContent = "", redirectTo = undefined} = {}
    ) => {
        const hasPermission = this.permissionPolicies.map(pP => pP.key).includes(permissionKey);
        if (!hasPermission) {
            const errorMessage =
                errorContent || i18n.t("stores.me.permissionError", {ns: "common"});
            switch (responseType) {
                case "message":
                    message.error(errorMessage);
                    break;
                case "modal":
                    appStore.openErrorModal({content: errorMessage});
                    break;
                case "redirection":
                    appStore.setCurrentPath(redirectTo);
                    break;
                default:
                    break;
            }
        }
        return hasPermission;
    };

    // Sayfa/aksiyon yetkisi (bkz. constants/pagePermissions). Alt kullanıcı dışındaki roller için
    // ek kısıt yoktur; onlarda yalnızca checkRole kontrolleri geçerlidir.
    @action checkPermission = permissionKey =>
        !this.isSubAgency || (this.auth?.pagePermissions ?? []).includes(permissionKey);

    @action canAccessPath = pathname =>
        !this.isSubAgency || isPathGranted(pathname, this.auth?.pagePermissions);

    @action login = async params => {
        await appStore.setLoading(true);
        this.sessionToken = undefined;
        const token = b64EncodeUnicode(params.username + ":" + params.password);
        baseRequest.addHeader(token);
        return baseRequest
            .post("/login")
            .then(async result => {
                if (result.status !== 200) {
                    appStore.openErrorModal({
                        content: result.data.message
                    });
                    return false;
                } else {
                    if (result.data.authenticated) {
                        this.fullName = result.data.user.fullName;
                        this.phone = result.data.user.phone;
                        this.username = result.data.user.username;
                        this.userId = result.data.user._id;
                        this.email = result.data.user.email;
                        this.company = result.data.user.company;
                        this.companies = result.data.user.companies || [];
                        const subcompanies = result.data.user.subcompanies || [];
                        this.subcompanies = subcompanies;
                        const authSubcompanies = result.data.user?.auth?.subcompanies || [];
                        this.isPoolCustomer =
                            ["cUser"].includes(result.data.user.role) &&
                            subcompanies.length > 0 &&
                            subcompanies
                                .filter(sc => authSubcompanies.includes(sc._id.toString()))
                                .every(subcompany => subcompany.subCompanyWorkType === "pool");
                        this.sessionToken = result.data.sessionToken;
                        this.role = result.data.user.role;
                        this.isSubAgency = result.data.user.isSubAgency === true;
                        this.auth = result.data.user.auth;
                        this.pool = result.data.user.pool;
                        this.isUatCustomer =
                            result.data.user.uat ||
                            process.env.REACT_APP_ENV === "development" ||
                            process.env.NODE_ENV === "development" ||
                            false;
                        this.isDedicatedCustomer =
                            result.data.user.role === "cUser" && !this.isPoolCustomer;
                        this.permissionHash = result.data.user?.permissionHash || "";
                        this.permissionPolicies = result.data.user?.permissionPolicies || [];
                        this.canCallCouriers = result.data.user?.canCallCouriers === true; // Only set true if explicitly true, otherwise false
                        this.alotech = result.data.user?.alotech || null; // Load AloTech configuration
                        this.operationPrivileges = result.data.user?.operationPrivileges || [];
                        const savedSubCompanyId = this.selectedSubCompanyId;
                        this.selectedSubCompanyId =
                            savedSubCompanyId &&
                            subcompanies.some(sc => sc._id === savedSubCompanyId)
                                ? savedSubCompanyId
                                : subcompanies.length > 0
                                ? subcompanies[0]._id
                                : null;
                        // TODO: refactor
                        if (!this.auth) {
                            this["auth"] = {};
                        }
                        if (!this.auth?.companies) {
                            this.auth["companies"] = [];
                        }
                        if (!this.auth?.subcompanies) {
                            this.auth["subcompanies"] = [];
                        }
                        if (!this.auth?.cities) {
                            this.auth["cities"] = [];
                        } else if (this.auth?.cities.length > 0) {
                            if (!this.auth?.cities.includes(this.map.filters.city)) {
                                this.map.filters.city = this.auth?.cities[0];
                                console.log(this.map.filters.city);
                                const city = cities.find(x => x.value == this.map.filters.city);
                                console.log(city);
                                this.map.settings.center = city.center;
                            }
                            // Canlı operasyon filtresi de yetkiye göre düzeltilir; aksi halde
                            // sayfa ilk render'ını varsayılan "istanbul" ile yapıp o şehrin
                            // verisini çekiyor.
                            // "Tümü" oturum içinde geçerli bir seçim ama kalıcı olmamalı:
                            // ülke/çok şehir geneli sorgu pahalı, kullanıcı gerçekten istiyorsa
                            // filtreden yeniden seçer (aynı kural Operasyon Yönetimi sayfasının
                            // açılışında da uygulanıyor).
                            if (!this.auth?.cities.includes(this.liveOperation.filters.city)) {
                                this.liveOperation.filters.city = this.auth?.cities[0];
                            }
                        }
                        baseRequest.addHeader(this.sessionToken);
                        appStore.getCompanyUserList();
                        appStore.getCompanyListFilter();
                        this.hasCodPaymentMethodSelectionChecked = false;
                        this.isLoggedIn = true;

                        // Kurye Günlük İşlemleri sekmesinin görünürlüğü kontrolü
                        if (["cUser"].includes(result.data.user.role) && subcompanies.length > 0) {
                            const result = await import("./stores").then(({default: stores}) =>
                                stores.poolStore.isSubcompanyDedicateIntegration({
                                    subCompanyId: subcompanies[0]._id.toString()
                                })
                            );
                            this.hasCourierDailyOperations = result || false;
                        } else {
                            this.hasCourierDailyOperations = false;
                        }
                    }
                    return result.data.authenticated;
                }
            })
            .finally(() => appStore.setLoading(false));
    };

    @action setSelectedSubCompanyId = subCompanyId => {
        this.selectedSubCompanyId = subCompanyId;
    };

    @action setCompany = comp => {
        this.company = comp;
    };

    @action logout = () => {
        return baseRequest.post("/logout").then(result => {
            if (result.data.logout) {
                optionStore.resetOptionsStoreData();
                this.resetMeStoreData();
                appStore.resetAppStoreData();
                baseRequest.addHeader(this.sessionToken);
            }
        });
    };

    @action sendResetMail = async params => {
        await appStore.setLoading(true);
        const resultOfRequest = await meService.sendResetMail(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        return resultOfRequest.data;
    };

    @action getEmailAndHash = async hash => {
        await appStore.setLoading(true);
        const resultOfRequest = await meService.getEmailAndHash(hash);
        await appStore.setLoading(false);
        return resultOfRequest;
    };

    @action resetPassword = async params => {
        await appStore.setLoading(true);
        const resultOfRequest = await meService.resetPassword(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.me.resetPassword.successMessage", {ns: "common"})
        });
        return true;
    };

    @action changePassword = async params => {
        await appStore.setLoading(true);
        // TO DO: authorization control backende eklenince requestte user dönecek, email buradan kaldırılabilir olacak.
        const resultOfRequest = await meService.changePassword(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.me.changePassword.successMessage", {ns: "common"})
        });
        return true;
    };

    @action changePasswordWithToken = async params => {
        await appStore.setLoading(true);
        const resultOfRequest = await meService.changePasswordWithToken(params);
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        appStore.showSuccessMessage({
            content: i18n.t("stores.me.changePasswordWithToken.successMessage", {ns: "common"})
        });
        return true;
    };

    @action checkRole = operation => {
        switch (operation) {
            case "isAdmin":
                return ["admin", "superadmin"].includes(this.role);
            // Entegre Vardiyalar ekranında şimşek ekleme/silme (bookSlot/cancelSlot).
            case "poolCourierShiftBooking":
                return ["admin", "superadmin", "agency"].includes(this.role);
            case "canSeeEquipmentRequestActions":
                return ["admin", "superadmin", "equipmentManager"].includes(this.role);
            case "sChef":
                return this.role === "sChef";
            case "selectUserRole":
                return [
                    "software",
                    "data",
                    "operationImproving",
                    "admin",
                    "superadmin",
                    "agency"
                ].includes(this.role);
            case "notVisibleForCompanyUser":
                return !["cUser", "sOperation", "sChef"].includes(this.role);
            case "isCompanyUser":
                return this.role === "cUser";
            case "isFinanceUser":
                return this.role === "finance";
            case "updateCourier":
                return [
                    "software",
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "admin",
                    "superadmin",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sChef"
                ].includes(this.role);
            case "newCourier":
                return [
                    "software",
                    "hr",
                    "hrEmployment",
                    "ik",
                    "admin",
                    "superadmin",
                    "oUser",
                    "bm",
                    "sy",
                    "sChef"
                ].includes(this.role);
            // Alt firmanın bağlı olduğu havuz "Dedike Entegrasyon" ise force atama ve sipariş
            // ayırma özellikleri otomatik açık; artık NewUser formunda elle işaretlenmiyor.
            case "canAssignForceCourier":
            case "canSplitDeliveries":
                return isDedicatedIntegrationCustomer(this);
            case "listCourierInfo":
                return [
                    "admin",
                    "superadmin",
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller",
                    "operationSupport",
                    "operationImproving",
                    "software",
                    "equipmentManager",
                    "data",
                    "cUser",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager"
                ].includes(this.role);
            case "createGetirPicture":
                return ["ik", "agencyIk", "admin", "superadmin"].includes(this.role);
            case "editProviderCompany":
                return ["ik", "agencyIk", "admin", "superadmin"].includes(this.role);
            case "showCreateCreditLogObjectionButton":
                return [
                    "admin",
                    "superadmin",
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller",
                    "operationSupport",
                    "operationImproving",
                    "software",
                    "data",
                    "cUser",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager",
                    "finance",
                    "marketing",
                    "sales"
                ].includes(this.role);
            case "listCourierWallet":
                return [
                    "admin",
                    "superadmin",
                    "finance",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "hr"
                ].includes(this.role);
            case "listCourierLatestStatus":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager"
                ].includes(this.role);
            case "createSubCompanyPaymentContract":
                return ["admin", "superadmin", "sales"].includes(this.role);
            case "cancelSubCompanyFuturePaymentContracts":
                return ["admin", "superadmin", "sales"].includes(this.role);
            case "listCourierHistory":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager",
                    "hr"
                ].includes(this.role);
            case "listCourierDedicationHistory":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager",
                    "hr"
                ].includes(this.role);
            case "listCourierStatusHistory":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager",
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk"
                ].includes(this.role);
            case "listCourierShifts":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager",
                    "hr"
                ].includes(this.role);
            case "listCourierShiftSuggestions":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager"
                ].includes(this.role);
            case "canSearchPools":
                return !["sOperation", "sChef"].includes(this.role);
            case "listCourierOnboarding":
                return [
                    "admin",
                    "superadmin",
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef"
                ].includes(this.role);
            case "courierRequests.showZoneManagmentFilter":
                return ["admin", "superadmin", "bm"].includes(this.role);
            case "linkCourierToCourierRequest":
                return ["admin", "superadmin", "ik", "bm", "sy", "agency", "agencyIk"].includes(
                    this.role
                );
            case "zoneManagementSelect":
                // Acente yalnızca bölgeye atanır, bölge müdürlüğü ataması yok.
                return ["admin", "superadmin", "bm", "equipmentManager"].includes(this.role);
            case "hideForOperationRole":
                return ["operationSupport"].includes(this.role);
            case "hideForEquipmentManagerRole":
                return ["equipmentManager"].includes(this.role);
            case "liveOperation.showZoneManagementFilter":
                // Acente yalnızca bölgeye atanır, bölge müdürlüğü ataması yok.
                return ["admin", "superadmin", "bm"].includes(this.role);
            case "liveOperation.showZoneFilter":
                // auth.zones taşıyan roller (bm/sy/ik/agency/agencyIk) + yönetici roller; bölgeyle
                // ilgisi olmayan roller bu ekranda bölge kavramını hiç görmez.
                return ["admin", "superadmin", "bm", "sy", "ik", "agencyIk", "agency"].includes(
                    this.role
                );
            case "liveOperation.updateShift":
                // Eski Vardiya Yönetimi ekranında (ShiftTable / PoolShiftTable) saat ve kurye
                // değişiminin açık bir rol kapısı yoktu; erişimi route'un rol listesi
                // belirliyordu. Aksiyonlar bu ekrana taşınırken aynı küme yazılı hale getirildi.
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "fieldManager",
                    "sOperation",
                    "sChef"
                ].includes(this.role);
            case "hideForBmSyRole":
                return ["bm", "agency", "sy"].includes(this.role);
            case "updateCourierCallRecords":
                return [
                    "admin",
                    "superadmin",
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller",
                    "oUser",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            case "listCourierCODInfo":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "fieldManager",
                    "hr"
                ].includes(this.role);
            case "isHrUser":
                return ["hr", "hrEmployment", "ik", "agencyIk"].includes(this.role);
            case "isEquipmentManagerUser":
                return ["equipmentManager"].includes(this.role);
            case "listCourierTransferCenterInfo":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "operationImproving",
                    "cUser",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sOperation",
                    "sChef",
                    "fieldManager"
                ].includes(this.role);
            case "updateCourierStatus":
                return [
                    "software",
                    "operationSupport",
                    "admin",
                    "superadmin",
                    "oUser",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            case "assignCourier":
                return [
                    "hr",
                    "software",
                    "admin",
                    "superadmin",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "ik",
                    "agencyIk",
                    "hr",
                    "hrEmployment"
                ].includes(this.role);
            case "orderDetail":
                return [
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller",
                    "operationSupport",
                    "data",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "software",
                    "marketing",
                    "admin",
                    "superadmin",
                    "cUser",
                    "oUser",
                    "sOperation",
                    "sChef"
                ].includes(this.role);
            case "courierDetail":
                return !["marketing", "cUser"].includes(this.role);
            case "updateOrder":
                return [
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin",
                    "cUser"
                ].includes(this.role);
            case "passOrder":
                return [
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin"
                ].includes(this.role);
            case "courierDocumentsApprove":
                return [
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller",
                    "admin",
                    "superadmin",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            case "defaultCourierEquipmentFiltersForEquipmentManager":
                return ["equipmentManager"].includes(this.role);
            case "generalReportsList":
                return [
                    "admin",
                    "superadmin",
                    "cUser",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "sChef",
                    "sOperation",
                    "operationSupport",
                    "software"
                ].includes(this.role);
            case "courierReport":
                return [
                    "admin",
                    "superadmin",
                    "cUser",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "ik",
                    "agencyIk",
                    "equipmentManager",
                    "sChef",
                    "sOperation",
                    "operationSupport",
                    "software",
                    "finance",
                    "hr"
                ].includes(this.role);
            case "noAccessForHrUser":
                return !["hr"].includes(this.role);
            case "defaultCourierEquipmentFiltersForOperation":
                return ["bm", "sy"].includes(this.role);
            case "assignCourierEquipment":
                return [
                    "admin",
                    "superadmin",
                    "bm",
                    "sy",
                    "equipmentManager",
                    "ik",
                    "agency",
                    "agencyIk"
                ].includes(this.role);
            case "returnCourierEquipment":
                return [
                    "admin",
                    "superadmin",
                    "bm",
                    "sy",
                    "equipmentManager",
                    "ik",
                    "agency",
                    "agencyIk"
                ].includes(this.role);
            case "createCourierEquipmentContract":
                return [
                    "admin",
                    "superadmin",
                    "bm",
                    "sy",
                    "equipmentManager",
                    "ik",
                    "agency",
                    "agencyIk"
                ].includes(this.role);
            case "uploadCourierEquipmentContract":
                return [
                    "admin",
                    "superadmin",
                    "bm",
                    "sy",
                    "equipmentManager",
                    "ik",
                    "agency",
                    "agencyIk"
                ].includes(this.role);
            case "approveCourierEquipmentContract":
                return ["admin", "superadmin", "equipmentManager"].includes(this.role);
            case "rejectCourierEquipmentContract":
                return ["admin", "superadmin", "equipmentManager"].includes(this.role);
            case "deleteWaitingAssignmentEquipment":
                return [
                    "admin",
                    "superadmin",
                    "bm",
                    "sy",
                    "equipmentManager",
                    "ik",
                    "agency",
                    "agencyIk"
                ].includes(this.role);
            case "viewCourierDocumentsRejectionHistory":
                return [
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller",
                    "admin",
                    "superadmin",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            case "downloadCourierDocuments":
                return [
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller",
                    "admin",
                    "superadmin",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            case "completeTrainingPhase":
                return [
                    "admin",
                    "superadmin",
                    "bm",
                    "sy",
                    "agency",
                    "ik",
                    "agencyIk",
                    "hrEmployment"
                ].includes(this.role);
            case "updateCourierZoneManually":
                return [
                    "admin",
                    "superadmin",
                    "bm",
                    "agency",
                    "ik",
                    "agencyIk",
                    "hrEmployment"
                ].includes(this.role);
            case "updatePreferredCourier":
                return [
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin"
                ].includes(this.role);
            case "updateForceCourier":
                return [
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin",
                    "sChef"
                ].includes(this.role);
            case "transferOrder":
                return [
                    "oUser",
                    "bm",
                    "sy",
                    "operationSupport",
                    "software",
                    "admin",
                    "superadmin",
                    "sChef"
                ].includes(this.role);
            case "cancelRepeatOrder":
                return [
                    "cUser",
                    "bm",
                    "agency",
                    "sy",
                    "operationSupport",
                    "oUser",
                    "software",
                    "admin",
                    "superadmin"
                    //"cUser" TODO: open when resend delivery request is ready for cUser
                ].includes(this.role);
            case "newCompany":
                return [
                    "software",
                    "admin",
                    "superadmin",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "seller"
                ].includes(this.role);
            case "updateCompany":
                return [
                    "software",
                    "admin",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "superadmin",
                    "cUser",
                    "seller"
                ].includes(this.role);
            case "updateSubCompanyPricingButton":
                return ["software", "admin", "oUser", "bm", "agency", "sy", "superadmin"].includes(
                    this.role
                );
            case "updateCompanyMergeOptions":
                return [
                    "software",
                    "admin",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "superadmin",
                    "cUser"
                ].includes(this.role);
            case "updateCompanyQueueOptions":
                return [
                    "software",
                    "admin",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "superadmin",
                    "cUser"
                ].includes(this.role);
            case "createCourierRequest":
                return ["superadmin", "admin", "bm", "ik", "agencyIk", "agency"].includes(
                    this.role
                );
            case "updateCourierRequest":
                return ["superadmin", "admin", "bm", "ik", "agencyIk", "agency"].includes(
                    this.role
                );
            case "closeCourierRequest":
                return ["superadmin", "admin", "bm", "ik", "agencyIk", "agency"].includes(
                    this.role
                );
            case "deleteCourierRequest":
                return ["superadmin", "admin"].includes(this.role);
            case "exportCourierRequestsList":
                return ["superadmin", "admin", "bm", "ik", "agencyIk", "agency"].includes(
                    this.role
                );
            case "updateCompanySlotOptions":
                return [
                    "software",
                    "admin",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "superadmin",
                    "cUser"
                ].includes(this.role);
            case "sendCourierDataToAccounting":
                return ["superadmin", "admin", "bm", "agency", "sy", "ik", "agencyIk"].includes(
                    this.role
                );
            case "newRequest":
                return [
                    "operationSupport",
                    "software",
                    "admin",
                    "superadmin",
                    "cUser",
                    "oUser",
                    "bm",
                    "sy",
                    "sOperation",
                    "sChef"
                ].includes(this.role);
            case "userPermission":
                return (
                    ["data", "operationImproving", "software", "admin", "superadmin"].includes(
                        this.role
                    ) ||
                    (this.role === "cUser" && this.company)
                );
            case "isPoolCustomer":
                const userIsCustomer = this.role === "cUser";
                const isPoolCustomer = this.isPoolCustomer;
                return userIsCustomer && isPoolCustomer;
            case "isUatCustomer":
                return this.isUatCustomer;
            case "isUatPoolCustomer":
                return this.isUatCustomer && this.isPoolCustomer;
            case "isCandidateCustomer":
                return (
                    this.isUatCustomer &&
                    //TODO : after db scripts run for all subcompanies, remove the first part("companies") of the condition
                    this.companies.every(company => company?.onboardingStatus === "candidate") &&
                    this.subcompanies.every(
                        subcompany => subcompany?.onboardingStatus === "candidate"
                    )
                );
            case "isNonApiIntegratedCustomer":
                const isCustomer = this.role === "cUser";
                return (
                    isCustomer &&
                    this.companies &&
                    this.companies.every(company => company.isApiIntegrationActive === false)
                );
            case "appConfig":
                return ["superadmin"].includes(this.role);
            case "isOperationUser":
                return ["oUser", "bm", "agency", "sy"].includes(this.role);
            case "isSOperationUser":
                return ["sOperation", "sChef"].includes(this.role);
            case "createWallet":
                return ["admin", "superadmin"].includes(this.role);
            case "showPoolsPageActionButtons":
                //hide for this roles
                return !["cUser", "sOperation", "sChef"].includes(this.role);
            case "company.assignedCouriers":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.orderMerge":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.orderQueue":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.shiftOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.proofOfDelivery":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.dynamicPricing":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.deliveryVerification":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.shiftSuggestion":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.barcodeOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.cutOffOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.CODPaymentOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.readyOrderNotificationOptions":
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "operationSupport",
                    "operationImproving",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "data"
                ].includes(this.role);
            case "company.suppliers":
                return ["admin", "superadmin", "operationSupport"].includes(this.role);
            case "addSubCompany":
                return ["admin", "superadmin", "software", "oUser", "bm", "sy", "seller"].includes(
                    this.role
                );
            case "changeSubcompanyOnboardingStatus":
                // Acente (ve dolayısıyla acente temsilcisi) firma/alt firma onboarding
                // durumunu değiştiremez.
                return [
                    "admin",
                    "superadmin",
                    "software",
                    "oUser",
                    "bm",
                    "sy",
                    "hr",
                    "hrEmployment",
                    "ik",
                    "agencyIk",
                    "seller"
                ].includes(this.role);
            case "subcompany.bilingDetail":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "finance"
                ].includes(this.role);
            case "subcompany.cariRecordsDetail":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "finance"
                ].includes(this.role);
            case "subcompany.creditDetail":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            case "subcompany.usersDetail":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "finance"
                ].includes(this.role);
            case "subcompany.poolIntensityAlert":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            case "subcompany.integrationList":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            // Yemek Kartı Entegrasyonları kaldırıldı (kullanılmıyor).
            // case "subcompany.mealCardIntegrations":
            //     return [
            //         "admin",
            //         "superadmin",
            //         "operationSupport",
            //         "oUser",
            //         "bm",
            //         "agency",
            //         "sy"
            //     ].includes(this.role);
            case "subcompany.salesDetail":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy"
                ].includes(this.role);
            case "subcompany.paymentsDetail":
                // "Ödemeler (Dedike)" sekmesi acenteye (ve acente temsilcisine) kapalı.
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "sy",
                    "finance"
                ].includes(this.role);
            case "subcompany.vigoPosDetail":
                return ["admin", "superadmin", "operationSupport", "oUser", "bm", "sy"].includes(
                    this.role
                );
            case "subcompany.contractsDetail":
                // "Sözleşmeler" sekmesi acenteye (ve acente temsilcisine) kapalı.
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "sy",
                    "finance"
                ].includes(this.role);
            case "createCreditLogObjection":
                return [
                    "admin",
                    "superadmin",
                    "operationSupport",
                    "oUser",
                    "bm",
                    "agency",
                    "sy",
                    "cUser"
                ].includes(this.role);
            case "updateCourierOssNumber":
                return ["admin", "superadmin", "ik", "agencyIk"].includes(this.role);
            case "updateCourierOssStartDate":
                return ["admin", "superadmin", "ik", "agencyIk"].includes(this.role);
            case "updateCourierMhNumber":
                return ["admin", "superadmin", "ik", "agencyIk"].includes(this.role);
            case "updateCourierActivations":
                return ["admin", "superadmin", "ik", "agencyIk"].includes(this.role);
            case "sendCourierToEvaluation":
                return ["admin", "superadmin", "sy", "agency"].includes(this.role);
            case "cancelCourierEvaluation":
            case "sendCourierToTermination":
                return ["admin", "superadmin", "bm", "agency"].includes(this.role);
            case "startCourierLeave":
            case "endCourierLeave":
                return ["admin", "superadmin", "bm", "agency", "sy"].includes(this.role);
            case "terminateCourier":
                return ["admin", "superadmin", "ik", "agency", "agencyIk"].includes(this.role);
            case "getCourierLeaveHistory":
                return [
                    "admin",
                    "superadmin",
                    "bm",
                    "agency",
                    "sy",
                    "ik",
                    "agencyIk",
                    "hr",
                    "hrEmployment",
                    "oUser"
                ].includes(this.role);
            case "sendAnnouncement":
                return (
                    ["admin", "superadmin", "bm", "agency"].includes(this.role) ||
                    this.hasOperationPrivilege("sendAnnouncementNotification")
                );
            case "sendWhatsappAnnouncement":
                return (
                    ["admin", "superadmin", "bm", "agency"].includes(this.role) ||
                    this.hasOperationPrivilege("sendWhatsAppAnnouncement")
                );
            case "sendAnnouncementWithExcel":
                return (
                    ["admin", "superadmin", "bm", "agency"].includes(this.role) ||
                    this.hasOperationPrivilege("sendAnnouncementNotificationByExcel")
                );
            case "sendWhatsappAnnouncementWithExcel":
                return (
                    ["admin", "superadmin", "bm", "agency"].includes(this.role) ||
                    this.hasOperationPrivilege("sendWhatsAppAnnouncementByExcel")
                );
            case "isSeller":
                return this.role === "seller";
            case "isAgency":
                return this.role === "agency";
            case "ticketAccess":
                return TICKET_BACKOFFICE_ROLES.includes(this.role);
            // firma/şube filtresi alt firma listesi yetkisi gerektirir (marketing rolüne kapalı)
            case "ticketCompanyFilter":
                return TICKET_BACKOFFICE_ROLES.includes(this.role) && this.role !== "marketing";
            case "ticketAdmin":
                return TICKET_ADMIN_ROLES.includes(this.role);
            case "applicationAccess":
                return APPLICATION_ROLES.includes(this.role);
            case "companyApplicationEdit":
                return APPLICATION_COMPANY_EDIT_ROLES.includes(this.role);
            case "applicationZoneManagementFilter":
                return APPLICATION_ZONE_MANAGEMENT_FILTER_ROLES.includes(this.role);
            // Acente başvuruları: admin rolleri ya da dinamik yetki politikası (düzenleme görmeyi kapsar)
            case "agencyApplicationView":
                return (
                    TICKET_ADMIN_ROLES.includes(this.role) ||
                    this.can(APPLICATION_PERMISSION_KEYS.viewAgency) ||
                    this.can(APPLICATION_PERMISSION_KEYS.editAgency)
                );
            case "agencyApplicationEdit":
                return (
                    TICKET_ADMIN_ROLES.includes(this.role) ||
                    this.can(APPLICATION_PERMISSION_KEYS.editAgency)
                );
            // İletişim formu talepleri: admin rolleri ya da viewOtherTickets dinamik yetki politikası
            case "otherTicketView":
                return (
                    TICKET_ADMIN_ROLES.includes(this.role) ||
                    this.can(TICKET_PERMISSION_KEYS.viewOther)
                );
            default:
                return false;
        }
    };

    @action hasOperationPrivilege = operationId =>
        (this.operationPrivileges || []).includes(operationId);

    @action setRole = role => {
        this.role = role;
    };
    @action setPermissionHash = permissionHash => {
        this.permissionHash = permissionHash;
    };

    @action setPermissionPolicies = permissionPolicies => {
        this.permissionPolicies = permissionPolicies;
    };

    @action resetMeStoreData = async () => {
        this.map.options.showSubCompanies = false;
        this.map.options.showDummyCouriers = false;
        this.map.options.showRegions = false;
        this.map.filters.city = "istanbul";
        this.map.settings.center = {lat: 41.11, lng: 29.11};
        this.map.settings.zoom = 11;
        this.map.filters.courierType = [];
        this.map.filters.courierStatus = [];
        this.map.filters.company = "";
        this.map.filters.subCompany = "";
        this.map.filters.courier = "";

        this.searchTextFilter = "";
        this.orderPage = {
            filters: {
                orderType: "current",
                deliveryStatusOverrides: [],
                companies: [],
                selectedSubCompanies: [],
                dateRange: []
            }
        };

        // courierDailyOperations is user preference, should not be reset on logout
        // this.courierDailyOperations = { ... }

        // selectedSubCompanyId is user preference, should not be reset on logout
        // this.selectedSubCompanyId = null;

        this.isLoggedIn = false;
        this.fullname = "";
        this.fullName = "";
        this.phone = "";
        this.username = "";
        this.email = "";
        this.company = undefined;
        this.companies = [];
        this.subcompanies = [];
        this.sessionToken = undefined;
        this.userId = undefined;
        this.role = "";
        this.isSubAgency = false;
        this.auth = {companies: [], subcompanies: [], cities: []};
        this.pool = undefined;
        this.isPoolCustomer = false;
        this.isUatCustomer = false;
        this.permissionHash = undefined;
        this.permissionPolicies = [];
        this.canCallCouriers = false;
        this.alotech = null;
        this.operationPrivileges = [];
        this.hasCodPaymentMethodSelectionChecked = false;
        this.isDedicatedCustomer = false;
    };

    @action getPermissionPolicies = async () => {
        await appStore.setLoading(true);
        const resultOfRequest = await userService.getCurrentUsersPermissionPolicies();
        await appStore.setLoading(false);
        if (resultOfRequest.status !== 200) {
            appStore.openErrorModal({content: resultOfRequest.data.message});
            return false;
        }
        const {permissionPolicies = []} = resultOfRequest.data;
        this.setPermissionPolicies(permissionPolicies);
        return resultOfRequest.data;
    };

    @action setHasCodPaymentMethodSelectionChecked = () => {
        this.hasCodPaymentMethodSelectionChecked = true;
    };

    @action setCourierDailyOperationsPaymentTypes = paymentTypes => {
        this.courierDailyOperations.paymentTypes = paymentTypes;
    };

    @action setIsDedicatedCustomer = () => {
        this.isDedicatedCustomer = true;
    };
}
export default new meStore();
